import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, Rocket, Zap, Brain, Code, Database, TrendingUp, CheckCircle, Calendar, Clock, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import tulioIcon from './assets/tulio.png'

const StartupsPage = () => {
  const startupServices = [
    {
      id: 'mvp-development',
      title: 'MVP Development',
      description: 'Launch your product in 8-12 weeks with our rapid development approach',
      icon: Code,
      color: 'from-emerald-500 to-teal-600',
      pricing: 'Starting $25k',
      timeline: '8-12 weeks',
      features: ['Rapid prototyping', 'Core feature development', 'User testing', 'Market validation']
    },
    {
      id: 'ai-integration',
      title: 'AI-Powered Features',
      description: 'Add competitive AI capabilities to differentiate your product',
      icon: Brain,
      color: 'from-purple-500 to-indigo-600',
      pricing: 'Starting $15k',
      timeline: '4-8 weeks',
      features: ['AI chatbots', 'Recommendation engines', 'Predictive analytics', 'Natural language processing']
    },
    {
      id: 'scaling-infrastructure',
      title: 'Scaling Infrastructure',
      description: 'Build for growth with cloud-native, scalable architecture',
      icon: Zap,
      color: 'from-orange-500 to-red-600',
      pricing: 'Starting $20k',
      timeline: '6-10 weeks',
      features: ['Auto-scaling setup', 'Database optimization', 'Performance monitoring', 'DevOps automation']
    },
    {
      id: 'data-analytics',
      title: 'Data & Analytics',
      description: 'Make data-driven decisions with comprehensive analytics',
      icon: Database,
      color: 'from-blue-500 to-cyan-600',
      pricing: 'Starting $18k',
      timeline: '6-8 weeks',
      features: ['User behavior tracking', 'Business intelligence', 'Custom dashboards', 'A/B testing']
    }
  ];

  const startupBenefits = [
    {
      title: 'Speed to Market',
      description: 'Launch 2-3x faster than traditional development',
      icon: Rocket,
      stats: '8-12 weeks to MVP'
    },
    {
      title: 'Cost Efficiency',
      description: '40-60% cost savings vs hiring full-time team',
      icon: DollarSign,
      stats: '40-60% savings'
    },
    {
      title: 'Expert Guidance',
      description: 'Fractional CTO and product strategy support',
      icon: TrendingUp,
      stats: '100+ startups advised'
    }
  ];

  const pricingPlans = [
    {
      name: 'Starter MVP',
      price: '$25k',
      duration: '8-12 weeks',
      description: 'Perfect for early-stage startups validating their concept',
      features: [
        'Core MVP development',
        'User authentication',
        'Basic admin panel',
        'Mobile responsive design',
        'Basic analytics setup',
        '3 months post-launch support'
      ],
      popular: false
    },
    {
      name: 'Growth Package',
      price: '$50k',
      duration: '12-16 weeks',
      description: 'Comprehensive solution for startups ready to scale',
      features: [
        'Full featured product',
        'AI/ML integrations',
        'Advanced analytics',
        'Payment processing',
        'Admin dashboard',
        'API development',
        '6 months support & optimization'
      ],
      popular: true
    },
    {
      name: 'Enterprise Ready',
      price: '$100k+',
      duration: '16-24 weeks',
      description: 'Enterprise-grade platform for rapid scaling',
      features: [
        'Scalable architecture',
        'Advanced security',
        'Multi-tenant support',
        'Third-party integrations',
        'Custom AI features',
        'DevOps & monitoring',
        '12 months ongoing partnership'
      ],
      popular: false
    }
  ];

  const successStories = [
    {
      name: 'HealthTech Startup',
      industry: 'Healthcare',
      result: 'Series A $5M raised',
      description: 'Telemedicine platform serving 50K+ patients',
      timeline: '14 weeks'
    },
    {
      name: 'FinTech SaaS',
      industry: 'Finance',
      result: '10x user growth',
      description: 'AI-powered investment platform',
      timeline: '16 weeks'
    },
    {
      name: 'EdTech Platform',
      industry: 'Education',
      result: '$2M ARR achieved',
      description: 'Online learning marketplace',
      timeline: '18 weeks'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            <Navigation />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">For Startups</Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                From Idea to
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  Market Leader
                </span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Launch faster, scale smarter, and raise capital with our proven startup development methodology. We've helped 100+ startups go from concept to successful funding.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Free Consultation
                </Button>
                <Link to="/case-studies">
                  <Button size="lg" variant="outline">
                    View Success Stories
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center space-x-8 text-slate-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm">100+ Startups Launched</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm">$50M+ Raised by Clients</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Startup Success Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">8-12</div>
                    <div className="text-sm opacity-90">Weeks to MVP</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">85%</div>
                    <div className="text-sm opacity-90">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">3x</div>
                    <div className="text-sm opacity-90">Faster Launch</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">60%</div>
                    <div className="text-sm opacity-90">Cost Savings</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Startups Choose Tulio Labs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {startupBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                    <p className="text-slate-600 mb-4">{benefit.description}</p>
                    <div className="text-2xl font-bold text-emerald-600">{benefit.stats}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services for Startups */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Services Tailored for Startups</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Everything you need to build, launch, and scale your startup successfully.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {startupServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-emerald-200">
                  <CardContent className="p-8">
                    <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                    <p className="text-slate-600 mb-6">{service.description}</p>
                    
                    <div className="flex justify-between items-center mb-6 text-sm">
                      <div className="flex items-center text-slate-600">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {service.pricing}
                      </div>
                      <div className="flex items-center text-slate-600">
                        <Clock className="w-4 h-4 mr-1" />
                        {service.timeline}
                      </div>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button className="w-full bg-slate-900 hover:bg-slate-800">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Startup-Friendly Pricing</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Transparent, value-driven pricing designed for startup budgets and growth stages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Card key={index} className={`relative ${plan.popular ? 'ring-2 ring-emerald-500 shadow-xl' : ''}`}>
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-emerald-600 text-white">
                    Most Popular
                  </Badge>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-emerald-600 mb-2">{plan.price}</div>
                  <div className="text-slate-600 mb-4">{plan.duration}</div>
                  <p className="text-slate-600 mb-6">{plan.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                    onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Startup Success Stories</h2>
            <p className="text-xl text-slate-600">See how we've helped startups achieve their goals</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Badge className="mb-4" variant="secondary">{story.industry}</Badge>
                  <h3 className="text-lg font-bold mb-2">{story.name}</h3>
                  <div className="text-2xl font-bold text-emerald-600 mb-3">{story.result}</div>
                  <p className="text-slate-600 mb-4">{story.description}</p>
                  <div className="flex items-center text-sm text-slate-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {story.timeline} to launch
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Launch Your Startup?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join 100+ successful startups who trusted Tulio Labs to bring their vision to life. Let's build something extraordinary together.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Start Your Journey Today
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 px-8 py-4">
                Explore All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
              <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/startups" className="hover:text-white transition-colors">Startups</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StartupsPage;